import { colord, extend } from "colord";
import namesPlugin from "colord/plugins/names";
import cmykPlugin from "colord/plugins/cmyk";
import hwbPlugin from "colord/plugins/hwb";
import labPlugin from "colord/plugins/lab";

extend([namesPlugin, cmykPlugin, hwbPlugin, labPlugin]);

export interface ColorFormats {
  hex: string; rgb: string; hsl: string; hsv: string; cmyk: string; hwb: string; lab: string; oklch: string; name: string; css: string;
}

/** Accepts HEX (3/4/6/8 digits, # optional), rgb(), hsl(), hwb(), cmyk-less names like "tomato". Returns null if unreadable. */
export function parseColor(input: string) {
  let s = input.trim();
  if (!s) return null;
  if (/^[0-9a-f]{3,8}$/i.test(s) && [3, 4, 6, 8].includes(s.length)) s = "#" + s;
  // Comma-less modern syntax "rgb(255 0 0 / 50%)" and "255, 0, 0" bare triples
  if (/^\d{1,3}\s*[, ]\s*\d{1,3}\s*[, ]\s*\d{1,3}$/.test(s)) s = `rgb(${s.replace(/\s+/g, ",").replace(/,+/g, ",")})`;
  const ok = parseOklch(s);
  if (ok) return ok.color;
  const c = colord(s);
  return c.isValid() ? c : null;
}

const linToSrgb = (v: number) => (v <= 0.0031308 ? 12.92 * v : 1.055 * Math.pow(v, 1 / 2.4) - 0.055);
const srgbToLin = (v: number) => (v <= 0.04045 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4));

/** sRGB (0-255) to OKLCH: lightness 0-1, chroma, hue in degrees (Bjorn Ottosson's OKLab). */
export function rgbToOklch(r: number, g: number, b: number): { l: number; c: number; h: number } {
  const [lr, lg, lb] = [r, g, b].map((v) => srgbToLin(v / 255));
  const l = Math.cbrt(0.4122214708 * lr + 0.5363325363 * lg + 0.0514459929 * lb);
  const m = Math.cbrt(0.2119034982 * lr + 0.6806995451 * lg + 0.1073969566 * lb);
  const sc = Math.cbrt(0.0883024619 * lr + 0.2817188376 * lg + 0.6299787005 * lb);
  const L = 0.2104542553 * l + 0.793617785 * m - 0.0040720468 * sc;
  const A = 1.9779984951 * l - 2.428592205 * m + 0.4505937099 * sc;
  const B = 0.0259040371 * l + 0.7827717662 * m - 0.808675766 * sc;
  const c = Math.hypot(A, B);
  const h = c < 0.0004 ? 0 : ((Math.atan2(B, A) * 180) / Math.PI + 360) % 360;
  return { l: L, c, h };
}

function oklchToRgb(L: number, C: number, hDeg: number): { r: number; g: number; b: number; inGamut: boolean } {
  const a = C * Math.cos((hDeg * Math.PI) / 180), b = C * Math.sin((hDeg * Math.PI) / 180);
  const l = Math.pow(L + 0.3963377774 * a + 0.2158037573 * b, 3);
  const m = Math.pow(L - 0.1055613458 * a - 0.0638541728 * b, 3);
  const s = Math.pow(L - 0.0894841775 * a - 1.291485548 * b, 3);
  const lin = [4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s, -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s, -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s];
  const inGamut = lin.every((v) => v >= -0.0005 && v <= 1.0005);
  const [r, g, bl] = lin.map((v) => Math.round(Math.min(1, Math.max(0, linToSrgb(Math.min(1, Math.max(0, v))))) * 255));
  return { r, g, b: bl, inGamut };
}

/** Reads oklch(58.5% 0.233 277 / 0.5) or oklch(0.585 0.233 277); colours outside sRGB are clipped and flagged. */
export function parseOklch(input: string): { color: ReturnType<typeof colord>; inGamut: boolean } | null {
  const m = /^oklch\(\s*([\d.]+)(%?)\s+([\d.]+)(%?)\s+(-?[\d.]+)(deg)?\s*(?:\/\s*([\d.]+)(%?))?\s*\)$/i.exec(input.trim());
  if (!m) return null;
  const L = parseFloat(m[1]) / (m[2] ? 100 : 1);
  const C = parseFloat(m[3]) * (m[4] ? 0.4 / 100 : 1);
  const H = parseFloat(m[5]);
  const alpha = m[7] === undefined ? 1 : Math.min(1, Math.max(0, parseFloat(m[7]) / (m[8] ? 100 : 1)));
  if (![L, C, H].every(Number.isFinite) || L < 0 || L > 1.0001 || C < 0) return null;
  const { r, g, b, inGamut } = oklchToRgb(L, C, H);
  return { color: colord({ r, g, b, a: alpha }), inGamut };
}

const f1 = (n: number) => +n.toFixed(1);

/**
 * Plain-words notices for input that was accepted but changed on the way in: channels past their range are clamped
 * (rgb(300, 0, 0) shows as red), and OKLCH colours outside sRGB are clipped. Empty when nothing was altered.
 */
export function inputNotices(input: string): string[] {
  const s = input.trim();
  const out: string[] = [];
  const ok = parseOklch(s);
  if (ok) {
    if (!ok.inGamut) out.push("This OKLCH colour is outside the sRGB range, so it is shown clipped to the nearest colour a screen can display.");
    return out;
  }
  let fn = "", args: string[] = [];
  const m = /^(rgba?|hsla?|hwb)\(\s*([^)]*)\)$/i.exec(s);
  if (m) { fn = m[1].toLowerCase().replace(/a$/, ""); args = m[2].split(/[\s,\/]+/).filter(Boolean); }
  else if (/^-?\d{1,4}\s*[, ]\s*-?\d{1,4}\s*[, ]\s*-?\d{1,4}$/.test(s)) { fn = "rgb"; args = s.split(/[\s,]+/); }
  if (!fn) return out;
  const num = (t: string) => parseFloat(t);
  const clampNote = (label: string, raw: string, lo: number, hi: number, unit: string, scale = 1) => {
    const v = num(raw) * scale;
    if (!Number.isFinite(v)) return;
    if (v > hi) out.push(`Clamped ${label} ${f1(v)}${unit}\u2192${hi}${unit}.`);
    else if (v < lo) out.push(`Clamped ${label} ${f1(v)}${unit}\u2192${lo}${unit}.`);
  };
  const names = fn === "rgb" ? ["red", "green", "blue"] : fn === "hsl" ? ["hue", "saturation", "lightness"] : ["hue", "whiteness", "blackness"];
  if (fn === "rgb") {
    args.slice(0, 3).forEach((t, i) => (t.endsWith("%") ? clampNote(names[i], t, 0, 100, "%") : clampNote(names[i], t, 0, 255, "")));
  } else {
    args.slice(1, 3).forEach((t, i) => clampNote(names[i + 1], t, 0, 100, "%"));
  }
  if (args[3] !== undefined) {
    const t = args[3];
    const v = t.endsWith("%") ? num(t) / 100 : num(t);
    if (Number.isFinite(v) && (v > 1 || v < 0)) out.push(`Clamped alpha ${f1(v)}\u2192${v > 1 ? 1 : 0}.`);
  }
  return out;
}

export function formatsOf(input: string): ColorFormats | null {
  const c = parseColor(input);
  if (!c) return null;
  const { r, g, b, a } = c.toRgb();
  const hsl = c.toHsl(), hsv = c.toHsv(), cmyk = c.toCmyk(), hwb = c.toHwb();
  const alpha = a < 1 ? `, ${+a.toFixed(3)}` : "";
  const hex = c.toHex();
  return {
    hex,
    rgb: a < 1 ? `rgba(${r}, ${g}, ${b}${alpha})` : `rgb(${r}, ${g}, ${b})`,
    hsl: a < 1 ? `hsla(${Math.round(hsl.h)}, ${Math.round(hsl.s)}%, ${Math.round(hsl.l)}%${alpha})` : `hsl(${Math.round(hsl.h)}, ${Math.round(hsl.s)}%, ${Math.round(hsl.l)}%)`,
    hsv: `hsv(${Math.round(hsv.h)}, ${Math.round(hsv.s)}%, ${Math.round(hsv.v)}%)`,
    cmyk: `cmyk(${cmyk.c}%, ${cmyk.m}%, ${cmyk.y}%, ${cmyk.k}%)`,
    lab: ((l) => `lab(${f1(l.l)}% ${f1(l.a)} ${f1(l.b)}${a < 1 ? ` / ${+a.toFixed(3)}` : ""})`)(c.toLab()),
    oklch: ((o) => `oklch(${f1(o.l * 100)}% ${+o.c.toFixed(3)} ${f1(o.h)}${a < 1 ? ` / ${+a.toFixed(3)}` : ""})`)(rgbToOklch(r, g, b)),
    hwb: `hwb(${Math.round(hwb.h)} ${Math.round(hwb.w)}% ${Math.round(hwb.b)}%${a < 1 ? ` / ${+a.toFixed(3)}` : ""})`,
    name: c.toName({ closest: true }) ?? "",
    css: `color: ${hex};`,
  };
}

/** WCAG 2.x contrast ratio of two colors (any parseable format), or null when either is unreadable. */
export function contrastOf(fg: string, bg: string): number | null {
  const fa = parseColor(fg), ba = parseColor(bg);
  if (!fa || !ba) return null;
  // Semi-transparent colors: the background sits over white, the text over the background.
  const over = (top: ReturnType<typeof colord>, under: { r: number; g: number; b: number }) => {
    const { r, g, b: bl, a } = top.toRgb();
    return colord({ r: r * a + under.r * (1 - a), g: g * a + under.g * (1 - a), b: bl * a + under.b * (1 - a) });
  };
  const b = over(ba, { r: 255, g: 255, b: 255 });
  const a = over(fa, b.toRgb());
  const lum = (c: ReturnType<typeof colord>) => {
    const { r, g, b: bl } = c.toRgb();
    const f = (v: number) => { const s = v / 255; return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4); };
    return 0.2126 * f(r) + 0.7152 * f(g) + 0.0722 * f(bl);
  };
  const l1 = lum(a), l2 = lum(b);
  return (Math.max(l1, l2) + 0.05) / (Math.min(l1, l2) + 0.05);
}

/** Nudges the text color lighter or darker (same hue) until it reaches the target ratio; null if already fine or unreadable. */
export function suggestForeground(fg: string, bg: string, target = 4.5): string | null {
  const f = parseColor(fg), b = parseColor(bg);
  if (!f || !b) return null;
  if ((contrastOf(fg, bg) ?? 0) >= target) return null;
  const bgLight = b.isLight();
  for (let step = 1; step <= 100; step++) {
    const cand = (bgLight ? f.darken(step / 100) : f.lighten(step / 100)).toHex();
    if ((contrastOf(cand, bg) ?? 0) >= target) return cand;
  }
  return bgLight ? "#000000" : "#ffffff";
}

/** Ratio for display: cut (not rounded) to 2 decimals so 4.499 never shows as 4.50 beside a failing badge. */
export function displayRatio(ratio: number): string {
  return (Math.floor(ratio * 100 + 1e-9) / 100).toFixed(2);
}

/**
 * What the converter shows for the current input: the parsed colour, or, for a typo, the last good colour (never a blank page or a
 * black swatch), plus the invalid flag and any clamp notices. Pass the returned `last` back in on the next call.
 */
export function resolveDisplay(input: string, last: ColorFormats | null): { shown: ColorFormats | null; invalid: boolean; notices: string[]; last: ColorFormats | null } {
  const parsed = formatsOf(input);
  const next = parsed ?? last;
  return { shown: next, invalid: input.trim() !== "" && !parsed, notices: parsed ? inputNotices(input) : [], last: next };
}
